import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCanonicalUrl, getMetaConfig } from '@/utils/seoConfig';

/**
 * SEO Hook for managing meta tags, canonical URLs, and robots directives
 * Handles country-specific pages with self-referential canonicals
 */
export function useSEO({ 
  title, 
  description, 
  canonical, 
  robots,
  hreflangAlternates = null 
}) {
  const location = useLocation();
  
  useEffect(() => {
    // Get SEO config for current path
    const metaConfig = getMetaConfig(location.pathname);
    const canonicalUrl = canonical !== null ? (canonical || getCanonicalUrl(location.pathname)) : null;
    
    // Set document title
    if (title) {
      document.title = title;
    } else if (metaConfig.title) {
      document.title = metaConfig.title;
    }
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description || metaConfig.description || '';
    
    // Set robots meta tag
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = robots || metaConfig.robots || 'index,follow';
    
    // Set canonical URL (CRITICAL: Self-referential for country pages)
    if (canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonicalUrl;
    } else {
      // Remove canonical link if canonicalUrl is null
      const existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
    }
    
    // Handle hreflang alternates if provided
    if (hreflangAlternates) {
      // Remove existing hreflang tags
      const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');
      existingHreflangs.forEach(tag => tag.remove());
      
      // Add new hreflang tags
      Object.entries(hreflangAlternates).forEach(([hreflang, url]) => {
        const hreflangLink = document.createElement('link');
        hreflangLink.rel = 'alternate';
        hreflangLink.hreflang = hreflang;
        hreflangLink.href = url;
        document.head.appendChild(hreflangLink);
      });
    }
    
    // Open Graph tags
    const ogTitle = title || metaConfig.title || 'ivoliq';
    const ogDesc = description || metaConfig.description || '';
    
    const ogTags = {
      'og:title': ogTitle,
      'og:description': ogDesc,
      'og:type': 'website',
      'og:url': canonicalUrl || `https://ivoliq.io${location.pathname}`,
      'og:site_name': 'ivoliq',
      'og:locale': 'de_DE',
      'twitter:card': 'summary_large_image',
      'twitter:title': ogTitle,
      'twitter:description': ogDesc,
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      const isOg = property.startsWith('og:');
      const selector = isOg 
        ? `meta[property="${property}"]` 
        : `meta[name="${property}"]`;
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        if (isOg) {
          tag.setAttribute('property', property);
        } else {
          tag.setAttribute('name', property);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Cleanup function
    return () => {
      // Note: We don't clean up meta tags on unmount as they should persist
      // until the next page sets new values
    };
  }, [title, description, canonical, robots, hreflangAlternates, location.pathname]);
}

/**
 * Specialized hook for country-specific invoice generator pages
 * Ensures self-referential canonicals and proper hreflang setup
 */
export function useCountryPageSEO(country) {
  const countryConfigs = {
    'deutschland': {
      title: 'Rechnung erstellen kostenlos Deutschland – 30 Sek. | ivoliq',
      description: 'Kostenloser Rechnungsgenerator für Deutschland: Erstelle rechtssichere Rechnungen nach §14 UStG in 30 Sek. Pflichtangaben, PDF-Download, ohne Anmeldung.',
      canonical: 'https://ivoliq.io/rechnung-erstellen-kostenlos-deutschland',
      hreflangAlternates: {
        'de-DE': 'https://ivoliq.io/rechnung-erstellen-kostenlos-deutschland',
        'de-AT': 'https://ivoliq.io/rechnung-erstellen-kostenlos-oesterreich',
        'de-CH': 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos'
      }
    },
    'oesterreich': {
      title: 'Rechnung erstellen Österreich – kostenlos | ivoliq',
      description: 'Kostenloser Rechnungsgenerator für Österreich: UID-Nr., 20 % USt und alle Pflichtangaben nach § 11 UStG. PDF in 30 Sekunden – ohne Anmeldung.',
      canonical: 'https://ivoliq.io/rechnung-erstellen-kostenlos-oesterreich',
      hreflangAlternates: {
        'de-DE': 'https://ivoliq.io/rechnung-erstellen-kostenlos-deutschland',
        'de-AT': 'https://ivoliq.io/rechnung-erstellen-kostenlos-oesterreich',
        'de-CH': 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos'
      }
    },
    'schweiz': {
      title: 'Rechnung erstellen kostenlos Schweiz + QR-Bill | ivoliq',
      description: 'Kostenloser Rechnungsgenerator für die Schweiz mit Swiss QR-Bill: Erstelle rechtssichere Rechnungen nach MWST-Gesetz in 30 Sek. IBAN-Validierung, ohne Anmeldung.',
      canonical: 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos',
      hreflangAlternates: {
        'de-DE': 'https://ivoliq.io/rechnung-erstellen-kostenlos-deutschland',
        'de-AT': 'https://ivoliq.io/rechnung-erstellen-kostenlos-oesterreich',
        'de-CH': 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos'
      }
    }
  };
  
  const config = countryConfigs[country];
  if (!config) {
    console.warn(`Unknown country: ${country}`);
    return;
  }
  
  useSEO({
    title: config.title,
    description: config.description,
    canonical: config.canonical, // SELF-REFERENTIAL
    robots: 'index,follow',
    hreflangAlternates: config.hreflangAlternates
  });
}

export default useSEO;
